/** 28.09 (владелец): карточка игры у админа зала. Стандартная «Информация о клиенте»
 *  (PlayersBadge → ClientInfoCard, с комментариями ДК), состав и МК с правками через заявки ОО
 *  (ждущие — пунктиром «⏳»), «как рассчитались игроки». Тап по карточке — подробности. */
import { useState } from "react";
import { PlayersBadge } from "../../components/shared";
import { ClientInfoCard } from "../../components/modal/ClientInfoCard";
import type { HallGame, HallOp, HallPay, HallReq } from "../../hooks/useHallDay";
import { Tea, Coin } from "./icons";
import h from "./hallAdmin.module.css";

export interface CardActions {
  onCrew: (op: HallOp) => void; onAdd: () => void;
  onMcOp: (mc: { id: string; name: string }, op: HallOp) => void; onMcAdd: (mc: { id: string; name: string }) => void; onMcNew: () => void;
  onPay: () => void;
  /** 26.10 (владелец): «админ зала тоже добавляет людей в Юнифанс — у каждой игры его персональный QR». */
  onQr: () => void;
}

const fmt = (n: number) => n.toLocaleString("ru");
function paySummary(p: HallPay): string {
  const parts = [["нал", p.cash], ["терм", p.terminal], ["перев", p.transfer], ["р/с", p.invoice], ["друг", p.other]] as const;
  const s = parts.filter(([, v]) => v).map(([k, v]) => `${k} ${fmt(v)}`).join(" · ");
  return `${s || "0"}${p.change_amount ? ` · сдача ${fmt(p.change_amount)}` : ""}`;
}

export function HallGameCard({ g, pending, payment, editable, act, nameOf }: {
  g: HallGame; pending: HallReq[]; payment: HallPay | undefined; editable: boolean; act: CardActions; nameOf: (id: string | null) => string;
}) {
  const [more, setMore] = useState(false);
  const [card, setCard] = useState(false);
  const noCrew = !g.crew.length && g.need > 0;
  /* ждущие ОО: снять — зачёркнутый чип; поставить — пунктирный «+Имя ⏳» */
  const leaving = new Set(pending.filter((r) => (r.kind === "remove" && r.booking_id === g.booking_id) || (r.kind === "move" && r.from_booking_id === g.booking_id)).map((r) => r.operator_id));
  const coming = pending.filter((r) => (r.kind === "add" || r.kind === "move") && r.booking_id === g.booking_id);
  const mcPend = (mc: string) => pending.filter((r) => r.booking_id === g.booking_id && r.mc_id === mc);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <>
      <article className={h.game} role="button" tabIndex={0} aria-expanded={more}
        onClick={() => setMore((m) => !m)}
        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setMore((m) => !m); } }}>
        <div className={h.gHead}>
          <span className={h.gTime}>{g.time}</span>
          {g.night ? <span className={h.tag}>ночь</span> : null}
          <span className={h.gQuest}>{g.quest}</span>
          <span className={h.gMeta} onClick={stop}>
            <PlayersBadge count={g.players} bookingId={g.booking_id} fallbackName={g.client} allow onOpen={() => setCard(true)} />
            {g.tea ? <span className={h.tea} title={g.tea_room ?? "Чайная"}><Tea /></span> : null}
            {g.prepaid ? <span className={h.paid} title="Задаток"><Coin />{g.prepaid_amount ? ` ${fmt(g.prepaid_amount)}` : ""}</span> : null}
          </span>
        </div>
        {g.comment ? <div className={h.dk} title={g.comment}>ДК: {g.comment}</div> : null}
        <div className={h.crew} onClick={stop}>
          {noCrew && !coming.length ? <span className={h.warn}>⚠ нет операторов</span> : null}
          {g.crew.map((o) => (
            <button key={o.id} type="button" disabled={!editable} onClick={() => act.onCrew(o)}
              className={`${h.chip} ${o.accepted ? h.chipOk : ""} ${leaving.has(o.id) ? h.chipOut : ""}`}
              title={leaving.has(o.id) ? "Снятие ждёт ОО" : editable ? "Переставить или снять" : undefined}>
              {o.name}{o.accepted ? " ✓" : ""}{leaving.has(o.id) ? " ⏳" : ""}
            </button>
          ))}
          {coming.map((r) => <span key={r.id} className={`${h.chip} ${h.chipWait}`} title="Ждёт ОО">+{nameOf(r.operator_id)} ⏳</span>)}
          {editable ? <button type="button" className={h.plus} aria-label="Добавить оператора (заявка ОО)" onClick={act.onAdd}>+</button> : null}
        </div>
        {g.mcs.map((m) => {
          const pend = mcPend(m.id);
          return (
            <div key={m.id} className={h.mc} onClick={stop}>
              <span className={h.mcName}>🎓 {m.name}</span>
              {!m.ops.length && !pend.some((r) => r.kind !== "mc_remove") ? <span className={h.warn}>⚠ никто</span> : null}
              {m.ops.map((o) => {
                const out = pend.some((r) => r.kind === "mc_remove" && r.operator_id === o.id);
                return <button key={o.id} type="button" disabled={!editable} className={`${h.chip} ${out ? h.chipOut : ""}`} onClick={() => act.onMcOp(m, o)}>{o.name}{out ? " ⏳" : ""}</button>;
              })}
              {pend.filter((r) => r.kind !== "mc_remove").map((r) => <span key={r.id} className={`${h.chip} ${h.chipWait}`}>{r.kind === "mc_new" ? "МК ждёт ОО" : `+${nameOf(r.operator_id)}`} ⏳</span>)}
              {editable ? <button type="button" className={h.plus} aria-label={`Ведущий МК ${m.name} (заявка ОО)`} onClick={() => act.onMcAdd(m)}>+</button> : null}
            </div>
          );
        })}
        {pending.filter((r) => r.kind === "mc_new" && r.booking_id === g.booking_id && !g.mcs.some((m) => m.id === r.mc_id)).map((r) => (
          <div key={r.id} className={h.mc}><span className={`${h.chip} ${h.chipWait}`}>{r.summary} ⏳</span></div>
        ))}
        <div className={h.acts} onClick={stop}>
          {editable ? <button type="button" className={h.link} onClick={act.onMcNew}>+ МК</button> : null}
          {editable ? <button type="button" className={h.link} onClick={act.onQr} title="Ваш QR: игроки сканируют и попадают в игру в Юнифансе">QR для игроков ›</button> : null}
          <button type="button" className={`${h.link} ${payment ? h.linkOk : ""}`} onClick={act.onPay} disabled={!editable && !payment}>
            {payment ? `Расчёт: ${paySummary(payment)} ›` : "Вписать расчёт ›"}
          </button>
        </div>
        {more ? (
          <div className={h.more} onClick={stop}>
            {g.client && g.client !== "[encrypted]" ? <div><span className={h.lbl}>Клиент</span> {g.client}</div> : null}
            {g.phone ? <div><span className={h.lbl}>Телефон</span> <a href={`tel:${g.phone}`}>{g.phone}</a></div> : null}
            {g.comment ? <div><span className={h.lbl}>Коммент ДК</span> {g.comment}</div> : null}
            {g.tea ? <div><span className={h.lbl}>Чайная</span> {g.tea_room ?? "да"}</div> : null}
            {g.birthday || g.competition ? <div><span className={h.lbl}>Отметки</span> {[g.birthday && "день рождения", g.competition && "соревнование"].filter(Boolean).join(", ")}</div> : null}
            {g.source ? <div><span className={h.lbl}>Источник</span> {g.source}</div> : null}
            <button type="button" className={h.link} onClick={() => setCard(true)}>Информация о клиенте ›</button>
          </div>
        ) : null}
      </article>
      {card ? <ClientInfoCard bookingId={g.booking_id} fallbackName={g.client || undefined} onClose={() => setCard(false)} /> : null}
    </>
  );
}
